import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, AlertCircle, Lightbulb, Bug, Star, HelpCircle } from 'lucide-react';

interface FeedbackData {
  type: 'bug' | 'feature' | 'improvement' | 'praise' | 'support';
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  userAgent: string;
  viewState: ViewState;
  userContext: UserContext;
  timestamp: string;
  userId?: string;
}

interface ViewState {
  currentPage: string;
  currentComponent: string;
  userActions: string[];
  formData?: Record<string, any>;
  errorMessages?: string[];
  loadingStates?: string[];
}

interface UserContext {
  userRole: string;
  userPreferences: Record<string, any>;
  recentActivity: string[];
  deviceInfo: {
    platform: string;
    browser: string;
    screenSize: string;
  };
}

interface FeedbackSystemProps {
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  theme?: 'light' | 'dark';
  autoCollect?: boolean;
  onFeedbackSubmit?: (feedback: FeedbackData) => void;
}

const FeedbackSystem: React.FC<FeedbackSystemProps> = ({
  position = 'bottom-right',
  theme = 'light',
  autoCollect = true,
  onFeedbackSubmit
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feature' | 'improvement' | 'praise' | 'support'>('feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [viewState, setViewState] = useState<ViewState>({
    currentPage: window.location.pathname,
    currentComponent: '',
    userActions: []
  });
  const [userContext, setUserContext] = useState<UserContext>({
    userRole: 'client',
    userPreferences: {},
    recentActivity: [],
    deviceInfo: {
      platform: navigator.platform,
      browser: navigator.userAgent,
      screenSize: `${window.innerWidth}x${window.innerHeight}`
    }
  });

  const feedbackRef = useRef<HTMLDivElement>(null);

  // Auto-collect view state
  useEffect(() => {
    if (!autoCollect) return;

    const updateViewState = () => {
      setViewState(prev => ({
        ...prev,
        currentPage: window.location.pathname,
        currentComponent: getCurrentComponent(),
        userActions: [...prev.userActions, `${new Date().toISOString()}: ${window.location.pathname}`].slice(-10)
      }));
    };

    // Update on route changes
    const handleRouteChange = () => {
      updateViewState();
    };

    // Update on user interactions
    const handleUserInteraction = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.tagName === 'INPUT') {
        updateViewState();
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('input', handleUserInteraction);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('input', handleUserInteraction);
    };
  }, [autoCollect]);

  const getCurrentComponent = (): string => {
    // Try to identify the current component based on URL or page content
    const path = window.location.pathname;
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/recipes')) return 'RecipeBook';
    if (path.includes('/guru')) return 'GuruConnect';
    if (path.includes('/analytics')) return 'Analytics';
    if (path.includes('/profile')) return 'UserProfile';
    return 'Unknown';
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;

    setIsSubmitting(true);

    const feedbackData: FeedbackData = {
      type: feedbackType,
      title: title.trim(),
      description: description.trim(),
      priority,
      category: category || getCurrentComponent(),
      userAgent: navigator.userAgent,
      viewState,
      userContext,
      timestamp: new Date().toISOString()
    };

    try {
      // Submit to backend or external service
      if (onFeedbackSubmit) {
        onFeedbackSubmit(feedbackData);
      } else {
        // Default submission to console (replace with actual API call)
        console.log('Feedback submitted:', feedbackData);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setIsOpen(false);
        resetForm();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPriority('medium');
    setCategory('');
    setFeedbackType('feature');
  };

  const getFeedbackIcon = () => {
    switch (feedbackType) {
      case 'bug': return <Bug className="w-4 h-4" />;
      case 'feature': return <Lightbulb className="w-4 h-4" />;
      case 'improvement': return <Star className="w-4 h-4" />;
      case 'praise': return <Star className="w-4 h-4" />;
      case 'support': return <HelpCircle className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getFeedbackColor = () => {
    switch (feedbackType) {
      case 'bug': return 'text-red-600 bg-red-50 border-red-200';
      case 'feature': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'improvement': return 'text-green-600 bg-green-50 border-green-200';
      case 'praise': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'support': return 'text-purple-600 bg-purple-50 border-purple-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (showSuccess) {
    return (
      <div className={`fixed ${position} m-4 z-50`}>
        <div className="bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
          <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          </div>
          <span className="font-medium">Thank you! Your feedback has been submitted.</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Feedback Button */}
      <div className={`fixed ${position} m-4 z-40`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white hover:bg-gray-700' 
              : 'bg-white text-gray-700 hover:bg-gray-50'
          } border border-gray-200`}
          title="Send feedback"
        >
          <MessageCircle className="w-5 h-5" />
        </button>
      </div>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div 
            ref={feedbackRef}
            className={`max-w-md w-full rounded-lg shadow-xl ${
              theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                {getFeedbackIcon()}
                <h3 className="font-semibold">Send Feedback</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Feedback Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">What type of feedback?</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { type: 'feature', label: 'Feature Request', icon: <Lightbulb className="w-4 h-4" /> },
                    { type: 'bug', label: 'Bug Report', icon: <Bug className="w-4 h-4" /> },
                    { type: 'improvement', label: 'Improvement', icon: <Star className="w-4 h-4" /> },
                    { type: 'praise', label: 'Praise', icon: <Star className="w-4 h-4" /> },
                    { type: 'support', label: 'Support', icon: <HelpCircle className="w-4 h-4" /> }
                  ].map(({ type, label, icon }) => (
                    <button
                      key={type}
                      onClick={() => setFeedbackType(type as any)}
                      className={`p-2 rounded border text-sm flex items-center space-x-2 transition-colors ${
                        feedbackType === type 
                          ? getFeedbackColor()
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {icon}
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Brief title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Add dark mode to recipe book"
                  className={`w-full px-3 py-2 rounded border ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Detailed description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us more about your idea or issue..."
                  rows={4}
                  className={`w-full px-3 py-2 rounded border ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium mb-2">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as any)}
                  className={`w-full px-3 py-2 rounded border ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  <option value="low">Low - Nice to have</option>
                  <option value="medium">Medium - Important</option>
                  <option value="high">High - Very important</option>
                  <option value="critical">Critical - Blocking issue</option>
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className={`w-full px-3 py-2 rounded border ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  <option value="">Select a category</option>
                  <option value="Dashboard">Dashboard</option>
                  <option value="RecipeBook">Recipe Book</option>
                  <option value="GuruConnect">Guru Connect</option>
                  <option value="Analytics">Analytics</option>
                  <option value="UserProfile">User Profile</option>
                  <option value="Authentication">Authentication</option>
                  <option value="Navigation">Navigation</option>
                  <option value="Mobile">Mobile Experience</option>
                  <option value="Performance">Performance</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Context Information */}
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                <div className="font-medium mb-1">Context (automatically collected):</div>
                <div>Page: {viewState.currentPage}</div>
                <div>Component: {viewState.currentComponent}</div>
                <div>Browser: {userContext.deviceInfo.browser.split(' ')[0]}</div>
                <div>Screen: {userContext.deviceInfo.screenSize}</div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200">
              <button
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!title.trim() || !description.trim() || isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Feedback</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackSystem; 